import React, { useEffect, useRef, useState } from 'react';
import {
  LinearProgressProps,
  CircularProgressProps,
  Box,
  Typography,
  SxProps,
  Theme,
} from '@mui/material';
import { StyledLinearProgress, StyledCircularProgress, ProgressContainer, StyledPillLabelProgress, StyledPillLabelFill, StyledPillLabelTextSplit } from './Progress.style';

export interface ProgressProps {
  /**
   * The variant of the progress indicator
   */
  variant?: 'linear' | 'circular' | 'pillLabel';
  /**
   * The value of progress (0-100)
   */
  value?: number;
  /**
   * The color of the progress indicator
   * Can be a theme color name or a custom color (hex, rgb, rgba, etc.)
   */
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'unicityBlue' | 'unicityGreen' | 'unicityPurple' | 'unicityOrange' | 'unicityNavy' | string;
  /**
   * The size of the progress indicator
   */
  size?: 'small' | 'medium' | 'large' | number;
  /**
   * Whether to show the progress value as text
   */
  showValue?: boolean;
  /**
   * Custom label to show with the progress
   */
  label?: string;
  /**
   * The thickness of the circular progress
   */
  thickness?: number;
  /**
   * Whether the progress is indeterminate
   */
  indeterminate?: boolean;
  /**
   * Additional props for LinearProgress
   */
  linearProgressProps?: Partial<LinearProgressProps>;
  /**
   * Additional props for CircularProgress
   */
  circularProgressProps?: Partial<CircularProgressProps>;
  /**
   * Additional styles for the progress container
   */
  sx?: SxProps<Theme>;
}

export const Progress: React.FC<ProgressProps> = ({
  variant = 'linear',
  value = 0,
  color = 'primary',
  size = 'medium',
  showValue = false,
  label,
  thickness = 4,
  indeterminate = false,
  linearProgressProps,
  circularProgressProps,
  sx,
}) => {
  const progressValue = Math.min(Math.max(value, 0), 100);

  // Animation on view logic
  const [animatedValue, setAnimatedValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if ((variant !== 'linear' && variant !== 'pillLabel') || indeterminate) return;
    const node = ref.current;
    if (!node) return;
    let observer: IntersectionObserver | null = null;
    let animationFrame: number;
    let startValue = animatedValue;
    let targetValue = progressValue;

    const animateToValue = () => {
      setAnimatedValue((prev) => {
        if (prev < targetValue) {
          const next = Math.min(prev + Math.max(1, Math.abs(targetValue - prev) / 15), targetValue);
          animationFrame = requestAnimationFrame(animateToValue);
          return next;
        } else if (prev > targetValue) {
          const next = Math.max(prev - Math.max(1, Math.abs(targetValue - prev) / 15), targetValue);
          animationFrame = requestAnimationFrame(animateToValue);
          return next;
        } else {
          cancelAnimationFrame(animationFrame);
          return targetValue;
        }
      });
    };

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && !hasAnimated) {
        setHasAnimated(true);
        setAnimatedValue(startValue);
        animationFrame = requestAnimationFrame(animateToValue);
      }
    };

    observer = new window.IntersectionObserver(handleIntersect, { threshold: 0.1 });
    observer.observe(node);

    // Animate to new value whenever progressValue changes
    if (hasAnimated) {
      startValue = animatedValue;
      targetValue = progressValue;
      animationFrame = requestAnimationFrame(animateToValue);
    }

    return () => {
      if (observer && node) observer.unobserve(node);
      cancelAnimationFrame(animationFrame);
    };
    // eslint-disable-next-line
  }, [progressValue, variant, indeterminate]);

  const getCircularSize = () => {
    if (typeof size === 'number') return size;
    
    switch (size) {
      case 'small':
        return 32;
      case 'large':
        return 64;
      default:
        return 40;
    }
  };

  const isThemeColor = (color: string): boolean => {
    return ['primary','secondary','success','error','warning','info','unicityBlue','unicityGreen','unicityPurple','unicityOrange','unicityNavy'].includes(color);
  };

  const getCircularProgressColor = (): CircularProgressProps['color'] => {
    return isThemeColor(color)
      ? (color as CircularProgressProps['color'])
      : 'primary';
  };

  if (variant === 'pillLabel') {
    return (
      <ProgressContainer ref={ref} sx={sx}>
        <StyledPillLabelProgress value={animatedValue} label={label || ''} color={color}>
          {/* Navy fill bar as background */}
          <StyledPillLabelFill value={animatedValue} color={color} />
          {/* Navy label behind, clipped to unfilled area */}
          <StyledPillLabelTextSplit value={animatedValue} color={color} contrast="dark">{label}</StyledPillLabelTextSplit>
          {/* White label in front, clipped to filled area */}
          <StyledPillLabelTextSplit value={animatedValue} color={color} contrast="light">{label}</StyledPillLabelTextSplit>
        </StyledPillLabelProgress>
      </ProgressContainer>
    );
  }

  if (variant === 'circular') {
    return (
      <ProgressContainer sx={sx}>
        <Box position="relative" display="inline-flex">
          <StyledCircularProgress
            variant={indeterminate ? 'indeterminate' : 'determinate'}
            value={progressValue}
            color={getCircularProgressColor()}
            size={getCircularSize()}
            thickness={thickness}
            {...circularProgressProps}
            sx={{
              ...(circularProgressProps?.sx || {}),
              ...(!isThemeColor(color) && {
                '& .MuiCircularProgress-circle': {
                  stroke: color,
                }
              })
            }}
          />
          {showValue && !indeterminate && (
            <Box
              position="absolute"
              top={0}
              left={0}
              bottom={0}
              right={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography
                variant="caption"
                component="div"
                color="textSecondary"
                fontSize={size === 'small' ? '0.625rem' : '0.75rem'}
              >
                {`${Math.round(progressValue)}%`}
              </Typography>
            </Box>
          )}
        </Box>
        {label && (
          <Typography variant="body2" color="textSecondary" mt={1} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}
          </Typography>
        )}
      </ProgressContainer>
    );
  }

  return (
    <ProgressContainer ref={ref} sx={sx}>
      {label && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography variant="body2" color="textSecondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, mr: 1 }}>
            {label}
          </Typography>
          {showValue && !indeterminate && (
            <Typography variant="body2" color="textSecondary" sx={{ flexShrink: 0 }}>
              {`${Math.round((!indeterminate ? animatedValue : progressValue))}%`}
            </Typography>
          )}
        </Box>
      )}
      <StyledLinearProgress
        variant={indeterminate ? 'indeterminate' : 'determinate'}
        {...(indeterminate ? {} : { value: animatedValue })}
        color={getCircularProgressColor()}
        size={size}
        {...linearProgressProps}
      />
    </ProgressContainer>
  );
}; 